import type { Timestamps, SoftDeletable, PaginationParams, DateRangeFilter, SortParams } from "./common";

// ─── Enums ────────────────────────────────────────────────────────────────────

/**
 * Order lifecycle:
 *   pendiente → pagado → entregado  (inventory deducted here)
 *                      ↘ cancelado  (inventory restored if was entregado)
 *   pendiente          → cancelado
 *   pagado             → cancelado
 */
export type OrderStatus =
  | "pendiente"
  | "pagado"
  | "entregado"
  | "cancelado";

export type OrderType = "venta" | "regalo";

export type PaymentMethod =
  | "efectivo"
  | "transferencia"
  | "tarjeta"
  | "otro"
  | "n/a";

// ─── Order detail line ────────────────────────────────────────────────────────

export interface OrderDetail extends Timestamps {
  id: number;
  pedido_id: number;
  producto_id: number;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
  notas: string | null;
  producto_nombre?: string;
}

export interface OrderDetailCreate {
  producto_id: number;
  cantidad: number;
  precio_unitario: number;
  notas?: string;
}

// ─── Order (pedido) ───────────────────────────────────────────────────────────

export interface Order extends Timestamps, SoftDeletable {
  id: number;
  numero_pedido: string;
  cliente_nombre: string;
  cliente_contacto: string | null;
  fecha_pedido: string;
  fecha_entrega: string | null;
  status: OrderStatus;
  tipo_pedido: OrderType;
  metodo_pago: PaymentMethod;
  total: number;
  descuento: number;
  pagado: boolean;
  notas: string | null;
  inventario_aplicado: boolean;
  detalles?: OrderDetail[];
}

// ─── Request/Response types ───────────────────────────────────────────────────

export interface OrderCreate {
  cliente_nombre: string;
  cliente_contacto?: string;
  fecha_pedido: string;
  fecha_entrega?: string;
  status?: OrderStatus;
  tipo_pedido?: OrderType;
  metodo_pago: PaymentMethod;
  descuento?: number;
  pagado?: boolean;
  notas?: string;
  detalles: OrderDetailCreate[];
}

export interface OrderUpdate extends Partial<Omit<OrderCreate, "detalles">> {
  detalles?: OrderDetailCreate[];
}

export interface OrderFilters extends PaginationParams, DateRangeFilter, SortParams {
  status?:      OrderStatus;
  tipo_pedido?: OrderType;
  search?:      string;
  metodo_pago?: PaymentMethod;
  producto_id?: number;
}

// ─── Inventory check response ─────────────────────────────────────────────────
// Returned by PUT /api/pedidos/:id when status → 'entregado' but stock is insufficient

export interface InsufficientStockError {
  code: "STOCK_INSUFICIENTE";
  message: string;
  faltantes: {
    insumo_id: number;
    insumo_nombre: string;
    requerido: number;
    disponible: number;
    unidad: string;
  }[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Revenue rule: only 'venta' orders that are PAID count as income.
 * Delivery status does not matter for revenue.
 */
export function esIngreso(order: Pick<Order, "tipo_pedido" | "pagado">): boolean {
  return order.tipo_pedido === "venta" && order.pagado === true;
}

/**
 * Both 'venta' and 'regalo' deduct inventory when status = 'entregado'.
 */
export function afectaInventario(order: Pick<Order, "status">): boolean {
  return order.status === "entregado";
}

/** Returns true if status transition should trigger inventory deduction. */
export function debeDescontarInventario(
  statusAnterior: OrderStatus,
  statusNuevo: OrderStatus
): boolean {
  return statusAnterior !== "entregado" && statusNuevo === "entregado";
}

/** Returns true if status transition should revert inventory. */
export function debeRevertirInventario(
  statusAnterior: OrderStatus,
  statusNuevo: OrderStatus,
  inventarioAplicado: boolean
): boolean {
  return (
    statusAnterior === "entregado" &&
    statusNuevo === "cancelado" &&
    inventarioAplicado
  );
}
